import { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/article";
import type { ArticleData } from "@/mocks/article";

function ArticleBlock({ block }: { block: Record<string, unknown> }) {
  switch (block.type) {
    case "heading":
      return (
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-5 leading-snug">
          {block.text as string}
        </h2>
      );

    case "paragraph":
      return (
        <p className="text-lg text-foreground-700 leading-[1.75] mb-5">
          {block.text as string}
        </p>
      );

    case "list":
      return (
        <ul className="space-y-3 mb-6">
          {(block.items as string[]).map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-lg text-foreground-700 leading-relaxed">
              <i className="ri-check-line text-accent-500 mt-1 flex-shrink-0 w-5 h-5 flex items-center justify-center"></i>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );

    case "table_intro":
      return (
        <p className="text-lg text-foreground-700 leading-[1.75] mb-5">
          {block.text as string}
        </p>
      );

    case "table":
      return (
        <div className="overflow-x-auto mb-8 rounded-xl border border-background-200/70">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-background-100">
                {(block.headers as string[]).map((h, i) => (
                  <th key={i} className={`px-5 py-3.5 text-sm font-semibold text-foreground-900 whitespace-nowrap ${i === 0 ? "" : "text-center"}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(block.rows as string[][]).map((row, ri) => (
                <tr key={ri} className={`border-t border-background-200/50 ${ri % 2 === 0 ? "bg-background-50" : "bg-background-100/50"}`}>
                  {row.map((cell, ci) => (
                    <td key={ci} className={`px-5 py-3.5 text-sm text-foreground-700 ${ci === 0 ? "font-medium" : "text-center"}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case "faq": {
      const items = block.items as { q: string; a: string }[];
      return <ArticleFAQ items={items} />;
    }

    default:
      return null;
  }
}

function ArticleFAQ({ items }: { items: { q: string; a: string }[] }) {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <div className="flex flex-col gap-3 mb-8">
      {items.map((faq, idx) => (
        <div key={idx} className="bg-background-100 border border-background-200/70 rounded-xl overflow-hidden">
          <button
            onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
            className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer hover:bg-background-200/50 transition-colors"
          >
            <span className="text-base font-semibold text-foreground-950 pr-4">{faq.q}</span>
            <i
              className={`ri-add-line text-foreground-500 flex-shrink-0 transition-transform duration-300 w-5 h-5 flex items-center justify-center ${
                openIdx === idx ? "rotate-45" : ""
              }`}
            ></i>
          </button>
          <div className={`overflow-hidden transition-all duration-300 ${openIdx === idx ? "max-h-96 pb-5" : "max-h-0"}`}>
            <p className="px-5 text-foreground-600 leading-relaxed text-sm md:text-base">{faq.a}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function GuidePage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const article: ArticleData | undefined = slug ? articles[slug] : articles["allchinabuy-sheet-complete-guide"];

  const siteUrl = import.meta.env.VITE_SITE_URL || "https://allchinabuy.run";

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-[70px] min-h-[60vh] flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The guide you are looking for does not exist.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // Related articles (3 random other articles)
  const relatedSlugs = Object.keys(articles).filter((s) => s !== article.slug).slice(0, 4);

  return (
    <>
      <Navbar />
      <main className="pt-[70px]">
        {/* Article Header */}
        <header className="py-16 md:py-24 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground-950 transition-colors whitespace-nowrap">Home</Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium whitespace-nowrap">Shopping Guides</span>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              <span className="text-foreground-950 whitespace-nowrap">{article.title.length > 40 ? article.title.slice(0, 40) + "..." : article.title}</span>
            </nav>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-[1.2] mb-6">
              {article.title}
            </h1>

            {/* Featured Image */}
            <div className="rounded-2xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full aspect-[2/1] object-cover object-top"
                loading="eager"
              />
            </div>

            {/* Meta */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                Written by: <strong className="text-foreground-800">{article.authorName}</strong>
              </span>
              <span className="hidden sm:block text-foreground-300">|</span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                Updated: <strong className="text-foreground-800">{article.updatedDate}</strong>
              </span>
              <span className="hidden sm:block text-foreground-300">|</span>
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center"></i>
                Reviewed by: <strong className="text-foreground-800">{article.reviewedBy}</strong>
              </span>
            </div>
          </div>
        </header>

        {/* Article Body */}
        <article className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-[720px] mx-auto">
            {article.content.map((block, idx) => (
              <ArticleBlock key={idx} block={block} />
            ))}

            {/* Internal Links Section */}
            <div className="mt-14 pt-10 border-t border-background-200/70">
              <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-5">Related Resources</h3>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="px-4 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:border-foreground-300 transition-all whitespace-nowrap cursor-pointer"
                >
                  AllChinaBuy Spreadsheet Directory
                </Link>
                <a
                  href="https://kaspreadsheet.com/category-911-Shoes.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-4 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:border-foreground-300 transition-all whitespace-nowrap cursor-pointer"
                >
                  Shoes Category
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-4 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:border-foreground-300 transition-all whitespace-nowrap cursor-pointer"
                >
                  Main Website
                </a>
                {relatedSlugs.map((rs) => (
                  <Link
                    key={rs}
                    to={`/guide/${rs}`}
                    className="px-4 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:border-foreground-300 transition-all whitespace-nowrap cursor-pointer"
                  >
                    {articles[rs].title.length > 35 ? articles[rs].title.slice(0, 35) + "..." : articles[rs].title}
                  </Link>
                ))}
              </div>
            </div>

            {/* Final CTA */}
            <div className="mt-12 text-center bg-background-100 rounded-2xl p-8 md:p-10">
              <h3 className="font-heading text-2xl font-bold text-foreground-950 mb-3">
                Ready to Start Exploring?
              </h3>
              <p className="text-foreground-600 mb-6 leading-relaxed">
                Head back to the AllChinaBuy Spreadsheet Directory and discover your next great find.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Back to Directory
                  <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-8 py-3 border-2 border-primary-500 text-primary-500 rounded-full text-sm font-semibold hover:bg-primary-500 hover:text-background-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  Explore Spreadsheet
                  <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </article>

        {/* Schema.org BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
              { "@type": "ListItem", "position": 2, "name": "Shopping Guides", "item": `${siteUrl}/#guides-section` },
              { "@type": "ListItem", "position": 3, "name": article.title }
            ]
          })}
        </script>

        {/* Schema.org Article */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "image": article.image,
            "author": { "@type": "Person", "name": article.authorName },
            "publisher": {
              "@type": "Organization",
              "name": "AllChinaBuy Spreadsheet Directory",
              "logo": {
                "@type": "ImageObject",
                "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/14bc629c50c04cb08f38590896596985.jpeg"
              }
            },
            "datePublished": "2026-06-25",
            "dateModified": "2026-06-25",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `${siteUrl}/guide/${article.slug}`
            }
          })}
        </script>
      </main>
      <Footer />
    </>
  );
}